import { useState, useEffect } from 'react';
import { X, Sparkles, ExternalLink, Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useMintSeasonNFT } from '@/hooks/usePointsLottery';
import type { NFTMintResponse, NFTTier } from '@/types/schemas';

interface NFTMintingModalProps {
  open: boolean;
  onClose: () => void;
  seasonId?: number;
  onViewGallery?: () => void;
}

interface StepIndicatorProps {
  step: string;
  label: string;
  status: 'pending' | 'active' | 'complete';
}

function StepIndicator({ step, label, status }: StepIndicatorProps) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          'flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all',
          status === 'active' && 'border-[#50E4F9] bg-[#50E4F9] animate-pulse',
          status === 'complete' && 'border-green-500 bg-green-500',
          status === 'pending' && 'border-white/30 bg-transparent'
        )}
      >
        {status === 'complete' ? (
          <Check size={16} className="text-white" />
        ) : (
          <span
            className={cn(
              'text-sm font-bold',
              status === 'active' ? 'text-white' : 'text-white/30'
            )}
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {step}
          </span>
        )}
      </div>
      <span
        className={cn(
          'text-sm',
          status === 'active' ? 'text-white' : 'text-white/50'
        )}
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {label}
      </span>
    </div>
  );
}

function getTierImage(tier: NFTTier): string {
  const tierMap = {
    diamond: '/nfts/DiamondTierNFT_JunkFun.png',
    black: '/nfts/BlackTierNFT_JunkFun.png',
    gold: '/nfts/GoldTierNFT_JunkFun.png',
    silver: '/nfts/SilverTierNFT_JunkFun.png',
    bronze: '/nfts/BronzeTierNFT_JunkFun.png',
  };
  return tierMap[tier];
}

function getTierGradient(tier: NFTTier): string {
  const gradients = {
    diamond: 'from-cyan-400 via-blue-500 to-purple-600',
    black: 'from-gray-700 via-gray-800 to-gray-900',
    gold: 'from-yellow-400 via-yellow-500 to-yellow-600',
    silver: 'from-gray-300 via-gray-400 to-gray-500',
    bronze: 'from-orange-600 via-orange-700 to-orange-800',
  };
  return gradients[tier];
}

function getTierName(tier: NFTTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function formatNumber(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '0';
  const num = typeof value === 'string' ? Number(value) : value;
  if (isNaN(num)) return '0';
  return num.toLocaleString();
}

export function NFTMintingModal({
  open,
  onClose,
  seasonId,
  onViewGallery,
}: NFTMintingModalProps) {
  const [mintStatus, setMintStatus] = useState<'idle' | 'preparing' | 'minting' | 'finalizing' | 'success'>('idle');
  const [mintResult, setMintResult] = useState<NFTMintResponse | null>(null);
  const [progress, setProgress] = useState(0);
  const mintMutation = useMintSeasonNFT();

  // Add shimmer animation styles
  useEffect(() => {
    if (!open) return;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
      @keyframes sparkle {
        0%, 100% { opacity: 0; transform: scale(0); }
        50% { opacity: 1; transform: scale(1); }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [open]);

  useEffect(() => {
    if (open && mintStatus === 'idle') {
      setMintStatus('preparing');
      setProgress(10);

      // Simulate preparing step
      setTimeout(() => {
        setMintStatus('minting');
        setProgress(40);

        // CRITICAL: Always pass seasonNo to ensure correct tier thresholds
        // Note: seasonId prop is actually the season number (e.g., 1, 2, 3), not database ID
        if (!seasonId) {
          console.error('[NFTMintingModal] ❌ ERROR: seasonId is required but not provided!');
          throw new Error('Season number is required for NFT minting');
        }
        const payload = { seasonNo: seasonId };
        console.log('[NFTMintingModal] 🚀 Calling mint API with payload:', payload);

        mintMutation.mutate(
          payload,
          {
            onSuccess: (data) => {
              console.log('[NFTMintingModal] ✅ Mint API Success:', data);
              setMintResult(data);
              setMintStatus('finalizing');
              setProgress(80);

              // Delay for animation
              setTimeout(() => {
                setProgress(100);
                setTimeout(() => {
                  setMintStatus('success');
                }, 300);
              }, 800);
            },
            onError: (error: Error) => {
              console.error('[NFTMintingModal] ❌ Mint API Error:', error);
              console.error('[NFTMintingModal] Error message:', error.message);
              console.error('[NFTMintingModal] Error stack:', error.stack);
              setMintStatus('idle');
              setProgress(0);
              onClose();
            },
          }
        );
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mintStatus]);

  const handleClose = () => {
    setMintStatus('idle');
    setMintResult(null);
    setProgress(0);
    onClose();
  };

  const handleViewGallery = () => {
    if (onViewGallery) {
      onViewGallery();
    }
    handleClose();
  };

  if (!open) {
    return null;
  }

  const getStepStatus = (step: 'preparing' | 'minting' | 'finalizing'): 'pending' | 'active' | 'complete' => {
    const steps = ['preparing', 'minting', 'finalizing'];
    const currentIndex = steps.indexOf(mintStatus === 'success' ? 'finalizing' : mintStatus);
    const stepIndex = steps.indexOf(step);

    if (stepIndex < currentIndex) return 'complete';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={mintStatus === 'success' ? handleClose : undefined}
      />

      {/* Modal */}
      <div
        className={cn(
          'relative w-full max-w-md max-h-[90vh] overflow-y-auto my-auto',
          'rounded-2xl shadow-2xl',
          mintStatus === 'success' && 'animate-bounce-in'
        )}
        style={{
          backgroundColor: 'rgba(40, 40, 40, 0.95)',
          border: '2px solid #50E4F9',
          boxShadow: '0 0 30px rgba(80, 228, 249, 0.3)',
        }}
      >
        {(mintStatus === 'preparing' || mintStatus === 'minting' || mintStatus === 'finalizing') && (
          <div className="p-8">
            {/* Sparkles Icon with Animation */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <Sparkles
                  size={80}
                  className="text-[#50E4F9]"
                  style={{ animation: 'float 2s ease-in-out infinite' }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-full rounded-full bg-[#50E4F9] opacity-20 animate-ping" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h2
              className="text-3xl font-bold text-center mb-2"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: '#50E4F9',
                textShadow: '0 0 20px rgba(80, 228, 249, 0.5)',
              }}
            >
              MINTING YOUR NFT
            </h2>

            {/* Step Message */}
            <p
              className="text-center text-white/70 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {mintStatus === 'preparing' && 'Preparing your NFT...'}
              {mintStatus === 'minting' && 'Creating your unique NFT...'}
              {mintStatus === 'finalizing' && 'Almost there! Finalizing...'}
            </p>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500, color: 'rgba(255, 255, 255, 0.8)' }}>
                <span>Progress</span>
                <span style={{ fontWeight: 600, color: '#50E4F9' }}>{progress}%</span>
              </div>
              <div
                className="h-3 rounded-full overflow-hidden relative"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              >
                <div
                  className="h-full transition-all duration-500 relative"
                  style={{
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #50E4F9, #00CED1)',
                  }}
                >
                  {/* Animated glow effect on progress bar */}
                  <div className="absolute inset-0 opacity-50" style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    animation: 'shimmer 2s infinite'
                  }} />
                </div>
              </div>
            </div>

            {/* Processing Steps */}
            <div className="space-y-3">
              <StepIndicator
                step="1"
                label="Preparing NFT Data"
                status={getStepStatus('preparing')}
              />
              <StepIndicator
                step="2"
                label="Generating NFT"
                status={getStepStatus('minting')}
              />
              <StepIndicator
                step="3"
                label="Finalizing Mint"
                status={getStepStatus('finalizing')}
              />
            </div>
          </div>
        )}

        {mintStatus === 'success' && mintResult && (
          <div>
            {/* Header */}
            <div
              className="relative p-6 text-center border-b-2"
              style={{
                borderColor: '#50E4F9',
                background: `linear-gradient(135deg, rgba(80, 228, 249, 0.2), rgba(80, 228, 249, 0.1))`,
              }}
            >
              <button
                onClick={handleClose}
                className={cn(
                  'absolute top-4 right-4 p-1 rounded-full',
                  'text-white/60 hover:text-white',
                  'hover:bg-white/10 transition-colors'
                )}
              >
                <X size={20} />
              </button>

              <div className="mb-4 relative">
                <Sparkles
                  size={48}
                  className="mx-auto text-[#50E4F9]"
                  style={{ animation: 'float 2s ease-in-out infinite' }}
                />
                {/* Sparkle particles */}
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute"
                    style={{
                      top: '50%',
                      left: '50%',
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      backgroundColor: '#50E4F9',
                      animation: `sparkle 1.5s ease-in-out ${i * 0.2}s infinite`,
                      transform: `translate(-50%, -50%) rotate(${i * 60}deg) translateX(30px)`,
                    }}
                  />
                ))}
              </div>

              <h2
                className="text-3xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', color: '#50E4F9' }}
              >
                NFT MINTED!
              </h2>

              <p
                className="text-white/70 text-sm"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Season #{mintResult.seasonNo} • Throne of Junk
              </p>
            </div>

            {/* NFT Preview */}
            <div className="p-6">
              {/* NFT Image */}
              <div
                className="relative mb-6 rounded-xl overflow-hidden border-4 transform transition-transform hover:scale-105"
                style={{ borderColor: '#50E4F9' }}
              >
                <img
                  src={getTierImage(mintResult.tier)}
                  alt={`${getTierName(mintResult.tier)} NFT`}
                  className="w-full h-auto"
                />
                <div
                  className={cn(
                    'absolute top-4 right-4 px-3 py-1 rounded-full',
                    'font-bold text-white text-sm shadow-lg',
                    `bg-gradient-to-r ${getTierGradient(mintResult.tier)}`
                  )}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {getTierName(mintResult.tier).toUpperCase()}
                </div>
              </div>

              {/* NFT Details */}
              <div
                className="space-y-3 mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <div
                  className="p-4 rounded-lg border"
                  style={{
                    borderColor: '#50E4F9',
                    backgroundColor: 'rgba(80, 228, 249, 0.1)',
                  }}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-white/60 mb-1">Tier</div>
                      <div className="text-white font-bold">
                        {getTierName(mintResult.tier)}
                      </div>
                    </div>
                    {mintResult.rank && (
                      <div>
                        <div className="text-xs text-white/60 mb-1">Rank</div>
                        <div className="text-[#FEBB01] font-bold">
                          #{mintResult.rank}
                        </div>
                      </div>
                    )}
                    <div className="col-span-2">
                      <div className="text-xs text-white/60 mb-1">
                        Points Contributed
                      </div>
                      <div className="text-white font-bold">
                        {formatNumber(mintResult.pointsContributed)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {onViewGallery && (
                  <button
                    onClick={handleViewGallery}
                    className="w-full px-6 py-3 rounded-full font-bold text-white transition-all hover:scale-105"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      background: 'linear-gradient(250deg, #7239EE 16.62%, #C374F9 71.51%)',
                    }}
                  >
                    <ExternalLink className="inline mr-2" size={20} />
                    VIEW IN GALLERY
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="w-full px-6 py-3 rounded-full font-bold text-white/90 border border-white/30 transition-all hover:bg-white/10"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
